import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { tap } from 'rxjs/internal/operators/tap';
import {
  IcdCodeMeta,
  type IcdCodeItem,
  type IcdCodeType,
} from '../../../../core/api/mhsConfigurationApiClient';
import { ResourceService } from '../../../../core/services/resource-service';
import { CodesService } from '../../services/codes-service';

@Component({
  selector: 'app-icd-manage-meta',
  imports: [
    FormsModule,
    NzCardModule,
    NzCheckboxModule,
    NzIconModule,
    NzCollapseModule,
    NzButtonModule,
    NzSpinComponent,
  ],
  templateUrl: './icd-manage-meta.html',
  styleUrl: './icd-manage-meta.scss',
})
export class IcdManageMeta {
  readonly modalRef: NzModalRef<any> = inject(NzModalRef);
  readonly codesService = inject(CodesService);
  readonly resourceService = inject(ResourceService);
  readonly messageService = inject(NzMessageService);
  readonly modalData = inject<{ codeId: number; codeType: IcdCodeType }>(NZ_MODAL_DATA);
  readonly codeId = this.modalData.codeId;
  readonly codeType = this.modalData.codeType;
  readonly codeData = signal<IcdCodeItem | null>(null);
  readonly nhfCodeAdditionalCriterial = signal<IcdCodeMeta[] | null>(null);
  readonly selectedSettlementCodes = signal<string[]>([]);
  readonly hasAdditionalCriteria = computed(
    () => (this.nhfCodeAdditionalCriterial()?.length ?? 0) > 0,
  );
  readonly hasAnyMeta = signal(true);
  readonly isLoading = signal(false);
  readonly isLoaded = signal(false);
  constructor() {
    this.loadCode();
  }
  loadCode() {
    this.resourceService
      .getNhfCodeAdditionalCriterial()
      .pipe(
        tap((result) => {
          this.nhfCodeAdditionalCriterial.set(
            result.map((item) => {
              const option = new IcdCodeMeta();
              option.code = item.code;
              option.name = item.name;

              return option;
            }),
          );
        }),
        switchMap(() => this.codesService.getCodeById(this.codeType, this.codeId)),
      )
      .subscribe({
        next: (result) => {
          this.codeData.set(result);
          this.isLoaded.set(true);

          const selectedCodes = result.meta?.['settlements']?.map((item) => item.code ?? '') ?? [];

          this.selectedSettlementCodes.set(selectedCodes);
          this.hasAnyMeta.set(selectedCodes.length === 0);
        },
        error: (error) => {
          console.error('Error loading code data:', error);
          this.isLoaded.set(true);
        },
      });
  }

  isSettlementSelected(code: string): boolean {
    return this.selectedSettlementCodes().includes(code);
  }

  settlementSelectionChange(code: string, selected: boolean): void {
    this.selectedSettlementCodes.update((codes) =>
      selected ? [...codes, code] : codes.filter((selectedCode) => selectedCode !== code),
    );
  }
  hasAnyMetaChanged(value: boolean): void {
    this.hasAnyMeta.set(value);
  }
  submit(): void {
    const selectedCodes = this.selectedSettlementCodes().map((code) => {
      const option = new IcdCodeMeta();
      option.code = code;
      option.name =
        this.nhfCodeAdditionalCriterial()?.find((item) => item.code === code)?.name ?? '';
      return option;
    });
    this.codesService.setMeta(this.codeType, this.codeId, 'settlements', selectedCodes).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.messageService.success('Kryteria zostały zapisane pomyślnie');
        this.modalRef.close();
      },
      error: (error) => {
        console.error('Error updating meta:', error);
        this.isLoading.set(false);
      },
    });
  }
}
